import React from 'react';
import { Button } from '../onyx/Button';
import { Panel } from '../onyx/Panel';
import { SectionHeader } from '../onyx/SectionHeader';
const CONSENT_STATUS_META_KEYS = new Set([
  'all_consents_completed',
  'required_documents',
  'document_meta',
  'flow_name',
  'questions_form_url',
]);

/**
 * Map document_type → display label from consent status ``document_meta``.
 * Accepts the API list shape ``[{document_type, label}]`` or a keyed object.
 */
const buildDocumentLabels = (consents) => {
  const labels = {};
  const meta = consents?.document_meta;
  if (Array.isArray(meta)) {
    meta.forEach((doc) => {
      if (doc?.document_type && doc.label) {
        labels[doc.document_type] = doc.label;
      }
    });
    return labels;
  }
  if (meta && typeof meta === 'object') {
    Object.entries(meta).forEach(([key, doc]) => {
      const label = doc?.label || doc?.title;
      if (label) {
        labels[key] = label;
      }
    });
  }
  return labels;
};

const defaultDocumentTitle = (docKey) =>
  `${docKey.charAt(0).toUpperCase() + docKey.slice(1)} Consent`;

const formatDateNoTz = (value) => {
  if (!value) return 'N/A';
  const [y, m, d] = String(value).split('-');
  return `${m}/${d}/${y}`;
};

/**
 * Read-only recap of a participant's signed consent documents, with
 * per-doc download buttons.
 */
export const ConsentSection = ({
  consents,
  completedAt,
  onDownload,
  loading = false,
}) => {
  if (loading || !consents) {
    return (
      <Panel className="dashboard-section">
        <SectionHeader
          title="Consent Documents"
          description={
            completedAt
              ? `Signed on ${new Date(completedAt).toLocaleDateString()}`
              : 'Loading...'
          }
        />
        <div className="onyx-detail-list">
          {[0, 1].map((k) => (
            <div key={k} className="onyx-detail-row skeleton-item">
              <span className="onyx-detail-label">
                <span className="skeleton skeleton-title" />
              </span>
              <span className="onyx-detail-value">
                <span className="skeleton skeleton-text" />
              </span>
              <span className="skeleton skeleton-button" />
            </div>
          ))}
        </div>
      </Panel>
    );
  }

  const documentLabels = buildDocumentLabels(consents);
  const entries = Object.entries(consents).filter(
    ([key, entry]) =>
      !CONSENT_STATUS_META_KEYS.has(key) &&
      entry &&
      typeof entry === 'object' &&
      !Array.isArray(entry) &&
      ('status' in entry || 'completedAt' in entry),
  );

  return (
    <Panel className="dashboard-section">
      <SectionHeader
        title="Consent Documents"
        description={
          completedAt
            ? `Signed on ${new Date(completedAt).toLocaleDateString()}`
            : null
        }
      />
      <div className="onyx-detail-list">
        {entries.map(([docKey, entry]) => (
          <div key={docKey} className="onyx-detail-row">
            <span className="onyx-detail-label">
              {documentLabels[docKey] || defaultDocumentTitle(docKey)}
            </span>
            <span className="onyx-detail-value">
              {entry.completedAt
                ? `Signed: ${formatDateNoTz(String(entry.completedAt).split('T')[0])}`
                : `Status: ${entry.status}`}
            </span>
            {onDownload && entry.document_id ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => onDownload(entry.document_id)}
              >
                Download
              </Button>
            ) : null}
          </div>
        ))}
      </div>
    </Panel>
  );
};

export default ConsentSection;
